import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { BuildingCatalogEntry, CastleResponse } from "../types/castle";

export const castleKeys = {
  state: ["castle"] as const,
  buildings: ["catalog", "buildings"] as const,
};

export function useCastle() {
  return useQuery({
    queryKey: castleKeys.state,
    queryFn: async () => {
      const res = await api.get<CastleResponse>("/castle");
      return res.data;
    },
    // Fixed 1 s poll while the castle query is active. The dynamic variant
    // (5 s idle, 1 s when orders are running) interacted badly with
    // mutation-side setQueryData — the interval wasn't re-evaluated on
    // mutation, so a build started during the 5 s idle window had to wait
    // out the full 5 s before the first poll caught the running order.
    // 1 s/req from a single active player is negligible; when the user
    // navigates away from Castle the query becomes inactive and stops polling.
    refetchInterval: 1000,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useBuildingCatalog() {
  return useQuery({
    queryKey: castleKeys.buildings,
    queryFn: async () => {
      const res = await api.get<BuildingCatalogEntry[]>("/catalog/buildings");
      const map = new Map<number, BuildingCatalogEntry>();
      for (const entry of res.data) map.set(entry.type, entry);
      return map;
    },
    staleTime: Infinity,
  });
}

export function useBuild() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (target: number) => {
      const res = await api.post<CastleResponse>("/castle/build", { target });
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(castleKeys.state, data);
    },
  });
}

export function useSpeedup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ target, seconds }: { target: number; seconds: number }) => {
      const res = await api.post<CastleResponse>("/castle/speedup", {
        target,
        seconds,
      });
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(castleKeys.state, data);
    },
  });
}
